const CONTINENT_CODES = {
  Africa: "AF",
  Asia: "AS",
  Europe: "EU",
  "Middle East": "ME",
  "North America": "NA",
  Oceania: "OC",
  "South America": "SA",
};

const normalizeLookupValue = (value) =>
  String(value ?? "")
    .trim()
    .toUpperCase();

const getPackages = (destination) =>
  Array.isArray(destination?.packages) ? destination.packages : [];

const buildFlagUrl = (destination) => {
  const isoCode = String(destination?.isoCode ?? "").trim().toLowerCase();

  return isoCode.length === 2 ? `https://flagcdn.com/${isoCode}.svg` : "";
};

const getUniqueCoverage = (destination) => {
  const coverageByKey = new Map();

  getPackages(destination).forEach((plan) => {
    (plan?.coverageCountries || []).forEach((coverage) => {
      const key =
        normalizeLookupValue(coverage?.iso3Code) ||
        normalizeLookupValue(coverage?.isoCode) ||
        normalizeLookupValue(coverage?.coverageName);

      if (!key || coverageByKey.has(key)) return;

      coverageByKey.set(key, {
        countryImageURL: buildFlagUrl(coverage),
        isoCode: coverage?.iso3Code || coverage?.isoCode || key,
        name: coverage?.coverageName || coverage?.name || key,
      });
    });
  });

  return Array.from(coverageByKey.values());
};

const normalizeDestination = (name, destination) => {
  const coverage = getUniqueCoverage(destination);

  return {
    countryImageURL: buildFlagUrl(destination),
    destinations: destination?.destinations_total ?? coverage.length,
    hasProduct: getPackages(destination).length > 0,
    isoCode: destination?.iso3Code || destination?.isoCode || name,
    name,
  };
};

const parsePlanSize = (value) => {
  const normalized = String(value ?? "").trim();
  if (normalized.toUpperCase() === "UL") return "UL";

  const numericValue = Number(normalized.replace(/\s*GB$/i, ""));
  return Number.isFinite(numericValue) ? numericValue : normalized;
};

const parsePlanDuration = (value) => {
  const normalized = String(value ?? "").trim();
  const match = normalized.match(/^\d+(?:\.\d+)?/);

  return match ? Number(match[0]) : normalized;
};

export const getEsimCatalog = (responseBody) => {
  const catalog = responseBody?.data?.countries;

  return catalog && typeof catalog === "object" ? catalog : {};
};

export const findCatalogDestination = (catalog, requestedCode) => {
  const lookup = normalizeLookupValue(requestedCode);
  if (!lookup) return null;

  for (const [sectionName, destinations] of Object.entries(catalog || {})) {
    if (!destinations || typeof destinations !== "object") continue;

    for (const [name, destination] of Object.entries(destinations)) {
      const candidates = [name, destination?.isoCode, destination?.iso3Code].map(
        normalizeLookupValue,
      );

      if (candidates.includes(lookup)) {
        return {
          destination,
          name,
          planType:
            sectionName === "Regions"
              ? "Regional"
              : sectionName === "Global"
                ? "Global"
                : "Local",
          sectionName,
        };
      }
    }
  }

  return null;
};

export const buildCountryGroups = (catalog) =>
  Object.entries(catalog || {})
    .filter(([sectionName]) => !["Regions", "Global"].includes(sectionName))
    .map(([continentName, destinations]) => ({
      [CONTINENT_CODES[continentName] || continentName]: Object.entries(
        destinations || {},
      )
        .map(([name, destination]) => normalizeDestination(name, destination))
        .sort((left, right) => left.name.localeCompare(right.name)),
    }));

const buildSectionOptions = (section) =>
  Object.entries(section || {}).map(([name, destination]) =>
    normalizeDestination(name, destination),
  );

export const buildCatalogOptions = (catalog) => ({
  global: buildSectionOptions(catalog?.Global),
  regional: buildSectionOptions(catalog?.Regions),
});

export const buildPlansForDestination = (catalog, requestedCode) => {
  const match = findCatalogDestination(catalog, requestedCode);
  if (!match) return [];

  return getPackages(match.destination).map((plan) => ({
    ...plan,
    activationPolicy: plan?.activationPolicy || "-",
    duration: parsePlanDuration(plan?.validity),
    hasProduct: true,
    initial_price: Number(plan?.displayprice ?? 0),
    isbought: false,
    offre: false,
    planCode: plan?.code,
    planId: plan?.code,
    planType: match.planType,
    price: Number(plan?.displayprice ?? 0),
    productId: plan?.productId,
    size: parsePlanSize(plan?.data),
    topup: Boolean(plan?.topup),
  }));
};

export const buildCoverageForDestination = (catalog, requestedCode) => {
  const match = findCatalogDestination(catalog, requestedCode);

  return match ? getUniqueCoverage(match.destination) : [];
};

export const buildCorporatePurchasePayload = (payload) => ({
  country: payload?.country,
  countryImage: payload?.countryImage,
  esimId: payload?.esimId || undefined,
  isoCode: payload?.isoCode,
  parentPlanType: payload?.parentPlanType || "",
  planId: payload?.planCode,
  productId: payload?.productId || payload?.planId,
  voucherAmount: payload?.voucherAmount,
});

export const buildCorporatePurchaseResponse = (responseBody, payload) => {
  const isTopUp = Boolean(payload?.esimId);
  const action = isTopUp ? "topped up" : "purchased";
  const destination = payload?.country ? ` ${payload.country}` : "";

  return {
    ...responseBody,
    data: {
      ...(responseBody?.data || {}),
      plan: {
        ...(responseBody?.data || {}),
        id: responseBody?.esimId,
      },
      Popup: {
        Title: "eSIM Payment Successful",
        globalCode: 0,
        flagCode: 0,
        Message: `You have successfully ${action} the${destination} eSIM.`,
        InstallEsim: !isTopUp,
        isPopup: true,
      },
    },
  };
};

